import {
  Box,
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Image,
  Text as ChakraText,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import { saveAs } from "file-saver";
import handlePrintReport from "../Reports/ImageSummaryReport";

const agGridStyles = `
  .custom-ag-grid.ag-theme-alpine .ag-root-wrapper {
    border-radius: 12px !important;
    overflow: hidden !important;
    border: 1px solid #e2e8f0 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header {
    background-color: #1a365d !important;
    border-top-left-radius: 12px !important;
    border-top-right-radius: 12px !important;
    height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-row {
    height: 70px !important;
    min-height: 70px !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    color: #ffffff !important;
    padding: 0 16px !important;
    border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-header-cell:last-child {
    border-right: none !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row {
    height: 64px !important;
    min-height: 64px !important;
    border-bottom: 1px solid #e2e8f0 !important;
    width: 100% !important;
    text-align: left !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-even {
    background-color: #f8fafc !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-row-odd {
    background-color: #ffffff !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell {
    display: flex !important;
    align-items: center !important;
    justify-content: flex-start !important;
    height: 64px !important;
    padding: 0 8px !important;
    font-size: 16px !important;
    color: #1a202c !important;
    font-weight: 500 !important;
    text-align: left !important;
    white-space: normal !important;
    word-break: break-word !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-cell.ag-numeric-cell {
    justify-content: center !important;
    text-align: center !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-panel {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding: 12px !important;
    background-color: #f7fafc !important;
    border-top: 1px solid #e2e8f0 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-page-summary-panel,
  .custom-ag-grid.ag-theme-alpine .ag-paging-button {
    font-size: 12px !important;
    color: #2d3748 !important;
  }
  .custom-ag-grid.ag-theme-alpine .ag-paging-button:hover {
    background-color: #e2e8f0 !important;
  }
`;

export default function DetailView() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const location = useLocation();
  const [selectedStore, setSelectedStore] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  const gridRef = useRef(null);

  const query = new URLSearchParams(location.search);
  const runId = query.get("runId") || "8";

  useEffect(() => {
    const fetchTableData = async (retryCount = 0) => {
      setLoading(true);
      setError(null);
      try {
        const url = `${API_CONFIG.BASE_URL}${
          API_CONFIG.ENDPOINTS.DETAILED_VIEW.IMAGE_COUNT
        }?runId=${encodeURIComponent(runId)}`;
        const response = await apiClient.get(url);
        const apiData = Array.isArray(response.data)
          ? response.data
          : [response.data];

        // Transform and fetch additional data per-row
        const transformedData = await Promise.all(
          apiData.map(async (item, idx) => {
            const imageName = item.fileName ?? "";
            const sno = item.sno ?? `SNO${idx + 1}`;
            const totalCount = item.totalCount ?? 0;
            const storeName = item.store?.storeName || "Unknown Store";

            let additionalData = [];
            let s3pathActualFile = "";
            let s3pathAnnotatedFile = "";
            try {
              // 1. Fetch additionalData (product info, s3 keys)
              const apiForProducts = encodeURI(
                `${
                  API_CONFIG.BASE_URL
                }${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.PRODUCTS_BY_IMAGE.replace(
                  ":runId",
                  runId
                ).replace(":imageName", imageName)}`
              );
              const secondResponse = await apiClient.get(apiForProducts);
              const secondDataArr = Array.isArray(secondResponse.data)
                ? secondResponse.data
                : [secondResponse.data];
              additionalData = secondDataArr.map((data) => ({
                file: data.fileName ?? "N/A",
                company:
                  data.product?.productCompany?.productCompanyName ?? "Unknown",
                brand:
                  data.product?.productBrand?.productBrandName ?? "Unknown",
                type: data.product?.packType ?? "Unknown",
                size: data.product?.packSize ?? "Unknown",
                count: data.productCount ?? 0,
              }));

              // 2. Use s3pathActualFile and s3pathAnnotatedFile from response!
              // For images, always use keys from secondDataArr[0] (first match).
              const s3actual = secondDataArr?.[0]?.s3pathActualFile;
              const s3annotated = secondDataArr?.[0]?.s3pathAnnotatedFile;

              // 3. Get presigned URLs only if key exists
              if (s3actual) {
                const { data: actualUrl } = await apiClient.get(
                  `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
                    s3actual
                  )}&bucketName=imageinsightimagesbeverages&operation=download`
                );
                s3pathActualFile = actualUrl;
              }
              if (s3annotated) {
                const { data: annotatedUrl } = await apiClient.get(
                  `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
                    s3annotated
                  )}&bucketName=imageinsightimagesbeverages&operation=download`
                );
                s3pathAnnotatedFile = annotatedUrl;
              }
            } catch (secondError) {
              additionalData = [];
            }

            return {
              rowIndex: idx,
              sno,
              storeName,
              imageName,
              count: totalCount.toString(),
              additionalData,
              s3pathActualFile,
              s3pathAnnotatedFile,
            };
          })
        );

        setTableData(transformedData);
      } catch (err) {
        if (retryCount < 2) {
          setTimeout(() => fetchTableData(retryCount + 1), 1000);
        } else {
          setError("Failed to fetch data after retries.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTableData();
  }, [runId]);

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedRowData(null);
  };

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleExportData = () => {
    const headers = [
      "S.No",
      "StoreName",
      "Image Name",
      "Count",
      "Nested File",
      "Nested Company",
      "Nested Brand",
      "Nested Pack Type",
      "Nested Pack Size",
      "Nested Count",
    ];
    const rows = tableData.flatMap((row) =>
      row.additionalData.length > 0
        ? row.additionalData.map((nested) =>
            [
              row.sno,
              row.storeName,
              row.imageName,
              row.count,
              nested.file || "",
              nested.company || "",
              nested.brand || "",
              nested.type || "",
              nested.size || "",
              nested.count || "",
            ]
              .map((value) => `"${value}"`)
              .join(",")
          )
        : [
            [
              row.sno,
              row.storeName,
              row.imageName,
              row.count,
              "",
              "",
              "",
              "",
              "",
              "",
            ]
              .map((value) => `"${value}"`)
              .join(","),
          ]
    );
    const csvContent = `${headers.join(",")}\n${rows.join("\n")}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const storeName = selectedStore ? selectedStore.storeName : "unknown";
    saveAs(blob, `detail_view_${storeName}.csv`);
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Additional",
        field: "expand",
        width: 80,
        cellRenderer: (params) => {
          const hasAdditionalData =
            params.data?.additionalData &&
            Array.isArray(params.data.additionalData) &&
            params.data.additionalData.length > 0;
          if (hasAdditionalData) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  onClick={() => handleViewDetails(params.data)}
                  style={{ cursor: "pointer" }}
                >
                  <FaAngleRight size={20} />
                </div>
              </div>
            );
          }
          return null;
        },
        sortable: false,
        filter: false,
      },
      {
        headerName: "S.No",
        field: "sno",
        cellClass: "ag-numeric-cell",
        sortable: true,
        filter: true,
        width: 150,
      },
      {
        headerName: "Store Name",
        flex:1,
        field: "storeName",
        minWidth: 80,
        sortable: true,
        filter: true,
        
        valueFormatter: (params) => params.value || "N/A",
      },
      {
        headerName: "Image Name",
        field: "imageName",
        flex:1,
          // increased
        minWidth: 80, 
        sortable: true,
        filter: true,
      },
      {
        headerName: "Count",
        field: "count",
        width:150,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        
      },
    ],
    []
  );

  const nestedColumnDefs = useMemo(
    () => [
      {
        headerName: "Company",
        field: "company",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Brand",
        field: "brand",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Type",
        field: "type",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Pack Size",
        field: "size",
        sortable: true,
        filter: true,
        valueFormatter: (params) => params.value || "Unknown",
      },
      {
        headerName: "Count",
        field: "count",
        width:90,
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        valueFormatter: (params) => params.value?.toString() || "0",
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      // flex: 1,
      minWidth: 80,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      cellStyle: { textAlign: "left" },
    }),
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <style>{agGridStyles}</style>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
          <Flex alignItems="center" gap="10px">
            <Button
              onClick={() =>
                handlePrintReport(
                  tableData,
                  selectedStore?.storeName || "unknown"
                )
              }
              size="sm"
              colorScheme="gray"
              variant="outline"
              borderRadius="5px"
              fontSize="sm"
              isDisabled={loading || error || tableData.length === 0}
            >
              Print Report
            </Button>
            <Button
              onClick={handleExportData}
              size="sm"
              bg="black"
              color="white"
              borderRadius="5px"
              fontSize="sm"
              _hover={{ bg: "gray.700" }}
              isDisabled={loading || error || tableData.length === 0}
            >
              Export Data
            </Button>
          </Flex>
        </Flex>
        <Box
          className="custom-ag-grid ag-theme-alpine"
          height="650px"
          width="100%"
          overflow="hidden"
        >
          <AgGridReact
            ref={gridRef}
            rowData={tableData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={64}
            headerHeight={70}
            pagination={true}
            paginationPageSize={8}
            suppressRowClickSelection={true}
            enableCellTextSelection={true}
            loadingOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="red.500">Loading...</ChakraText>
              </Flex>
            )}
            noRowsOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="gray.500">
                  {error || "No data available"}
                </ChakraText>
              </Flex>
            )}
            onGridReady={(params) => {
              if (loading) params.api.showLoadingOverlay();
              else if (tableData.length === 0 || error)
                params.api.showNoRowsOverlay();
              else params.api.hideOverlay();
            }}
          />
        </Box>
      </Card>

      <Modal
        isOpen={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        size="full"
      >
        <ModalOverlay />
        <ModalContent
          p={0}
          m={0}
          maxW="100vw"
          minW="100vw"
          maxH="100vh"
          minH="100vh"
          borderRadius="0"
        >
          <ModalHeader>Additional Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflow="auto">
            {selectedRowData && (
              <Box mb="20px">
                <Flex justify="space-between" align="center">
                  <ChakraText fontSize="lg" fontWeight="bold">
                    Store Name: {selectedRowData.storeName || "N/A"}
                  </ChakraText>
                  <ChakraText fontSize="lg" fontWeight="bold">
                    File Name: {selectedRowData.imageName || "N/A"}
                  </ChakraText>
                </Flex>
              </Box>
            )}
            {selectedRowData && (
              <Box>
                <Flex direction="row" gap="20px" height="600px" alignItems="flex-start">
                  {/* Grid Section */}
                  {selectedRowData &&
                  Array.isArray(selectedRowData.additionalData) &&
                  selectedRowData.additionalData.length > 0 ? (
                    <Box
                      className="custom-ag-grid ag-theme-alpine"
                      height="100%"
                      width="50%"
                    >
                      <AgGridReact
                        rowData={selectedRowData.additionalData}
                        columnDefs={nestedColumnDefs}
                        defaultColDef={defaultColDef}
                        rowHeight={50}
                        headerHeight={70}
                        pagination={true}
                        paginationPageSize={8}
                        suppressRowClickSelection={false}
                        enableCellTextSelection={true}
                        noRowsOverlayComponent={() => (
                          <Flex justify="center" align="center" height="100%">
                            <ChakraText color="gray.500">
                              No additional data available
                            </ChakraText>
                          </Flex>
                        )}
                      />
                    </Box>
                  ) : (
                    <ChakraText>No additional data available.</ChakraText>
                  )}
                  {/* Images Section */}
                  <Flex direction="column" gap="10px" alignItems="center" marginLeft="80px">
                    <ChakraText fontSize="lg" fontWeight="bold">
                      Actual Image
                    </ChakraText>
                    <Image
                      src={
                        selectedRowData.s3pathActualFile ||
                        "https://via.placeholder.com/300?text=No+Actual+Image"
                      }
                      alt="Actual"
                      maxW="800px"
                      maxH="700px"
                      w="120%"
                      h="700px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  </Flex>
                  <Flex direction="column" gap="10px" alignItems="center">
                    <ChakraText fontSize="lg" fontWeight="bold">
                      Annotated Image
                    </ChakraText>
                    <Image
                      src={
                        selectedRowData.s3pathAnnotatedFile ||
                        "https://via.placeholder.com/300?text=No+Annotated+Image"
                      }
                      alt="Annotated"
                      maxW="800px"
                      maxH="700px"
                      w="120%"
                      h="700px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  </Flex>
                </Flex>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleCloseDetailsModal}
              size="md"
              borderRadius="5px"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}